import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { Banner } from 'components/ui/banner';
import ProviderDropdown from './providerDropdown';
import CardReaderInput from './cardReaderSection';
import { PaymentSolutionTab } from 'state/paymentConfig/type';

interface IProps {
  defaultProvider: PAYMENT_CONFIG.IProcessors | undefined;
  setDefaultProvider: Dispatch<
    SetStateAction<PAYMENT_CONFIG.IProcessors | undefined>
  >;
  activeTab: PaymentSolutionTab;
  localTab: PaymentSolutionTab;
}

const Layout = ({
  defaultProvider,
  setDefaultProvider,
  activeTab,
  localTab,
}: IProps): JSX.Element => {
  const {
    paymentConfig,
    resetCardReaderValues,
    toGetPaymentDeviceLocation,
    toGetPosLocation,
    getCardReaderConfig,
    toFetchCardReaderValues,
    resetSaveCardReader,
    resetSaveDefaultCardReader,
    resetCreateDefaultConfig,
    toFetchDefaultProviders,
    toFetchDefaultConfigurations,
    toFetchChannelConfiguration,
  } = usePaymentConfig();

  const { error, saveCardReader, saveDefaultCardReader, createDefaultConfig } = paymentConfig;

  const [showErrorToast, setShowErrorToast] = useState(false);

  useEffect(() => {
    // `createPaymentDevice` and `editPaymentDevice` errors are surfaced as
    // toasts from addPaymentDeviceModal.tsx — don't also fire the generic
    // banner for them here.
    const errorKeys = [
      'getPaymentDevice',
      'deviceStatus',
      'deviceDeleted',
      'saveCardReader',
      'saveDefaultCardReader',
      'createDefaultConfiguration',
      'paymentConfiguration',
      'getPosLocation',
    ] as const;

    const hasError = errorKeys.some((key) => error?.[key]);
    if (hasError) {
      setShowErrorToast(true);
    }
  }, [error]);

  const [saveDeviceConfigSuccess, setSaveDeviceConfigSuccess] = useState(false);

  const [saveDefaultDeviceConfigSuccess, setSaveDefaultDeviceConfigSuccess] =
    useState(false);

  const [createDefaultConfigSuccess, setCreateDefaultConfigSuccess] =
    useState(false);

  useEffect(() => {
    resetCardReaderValues();
    // Clear any lingering banners when switching tabs/providers so a stale
    // error (or success) doesn't persist across contexts.
    setShowErrorToast(false);
    setSaveDeviceConfigSuccess(false);
    setSaveDefaultDeviceConfigSuccess(false);
    setCreateDefaultConfigSuccess(false);
    if (defaultProvider && activeTab === localTab) {
      toGetPaymentDeviceLocation(defaultProvider?.processorName);
      toGetPosLocation();
      getCardReaderConfig(defaultProvider?.processorName);
      toFetchCardReaderValues(defaultProvider?.processorId);
      toFetchChannelConfiguration(defaultProvider?.processorId);
    }
  }, [defaultProvider, activeTab, localTab]);

  useEffect(() => {
    if (saveCardReader && activeTab === localTab && defaultProvider) {
      toFetchCardReaderValues(defaultProvider?.processorId);
      setShowErrorToast(false);
      setSaveDeviceConfigSuccess(true);
      resetSaveCardReader();
    }
  }, [saveCardReader]);

  useEffect(() => {
    if (saveDefaultCardReader && activeTab === localTab) {
      toFetchDefaultProviders();
      setShowErrorToast(false);
      setSaveDefaultDeviceConfigSuccess(true);
      resetSaveDefaultCardReader();
    }
  }, [saveDefaultCardReader]);

  useEffect(() => {
    if (createDefaultConfig && activeTab === localTab) {
      setTimeout(() => {
        toFetchDefaultConfigurations();
        setShowErrorToast(false);
        setCreateDefaultConfigSuccess(true);
        resetCreateDefaultConfig();
      }, 200);

    }
  }, [createDefaultConfig]);

  const banners = [
    {
      show: showErrorToast,
      onClose: () => setShowErrorToast(false),
      message: 'Something went wrong! Please try again later.',
      variant: 'error' as const,
    },
    {
      id: 'saveConfig',
      show: saveDeviceConfigSuccess,
      onClose: () => setSaveDeviceConfigSuccess(false),
      message: 'Configuration saved',
      variant: 'success' as const,
    },
    {
      id: 'defaultConfig',
      show: saveDefaultDeviceConfigSuccess,
      onClose: () => setSaveDefaultDeviceConfigSuccess(false),
      message: 'Default configuration saved',
      variant: 'success' as const,
    },
    {
      id: 'createDefaultConfig',
      show: createDefaultConfigSuccess,
      onClose: () => setCreateDefaultConfigSuccess(false),
      message: 'Default configuration created successfully',
      variant: 'success' as const,
    },
  ];

  return (
    <>
      <ProviderDropdown
        defaultProvider={defaultProvider}
        setDefaultProvider={setDefaultProvider}
      />
      <CardReaderInput
        defaultProvider={defaultProvider}
        activeTab={activeTab}
        localTab={localTab}
      />

      {banners.map((banner) =>
        banner.show ? (
          <div key={banner.id || banner.message} className="mt-4 max-w-3xl">
            <Banner
              variant={banner.variant}
              message={banner.message}
              showClose
              onClose={banner.onClose}
            />
          </div>
        ) : null
      )}
    </>
  );
};

export default Layout;
